import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Wrench, 
  Calendar, 
  Clock, 
  DollarSign, 
  Plus, 
  CheckCircle,
  RefreshCw,
  X,
  Save,
  Download,
  HelpCircle,
  FileText,
  User,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import MaintenanceService from '../../services/maintenanceService';
import { useToast, ToastContainer } from '../common/Toast';
import { assetService } from '../../services/assetService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

// Project-style Action Dropdown (Maintenance Operations)
const ActionDropdown = ({
  maintenance,
  onView,
  onEdit,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 220);
    }
    setIsOpen(!isOpen);
  };

  return (
    <div
      className="relative"
      ref={dropdownRef}
      style={{ zIndex: isOpen ? 9999 : 'auto' }}
    >
      {/* Three-dot button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
        style={{ position: 'relative', zIndex: isOpen ? 10000 : 'auto' }}
      >
        <MoreHorizontal size={16} className="text-blue-500" />
      </button>

      {isOpen && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                onClick={() => {
                  setIsOpen(false);
                  onView(maintenance);
                }}
              >
                <Eye size={16} className="mr-2.5 text-gray-500 flex-shrink-0" />
                View Maintenance
              </button>
            </li>

            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                onClick={() => {
                  setIsOpen(false);
                  onEdit(maintenance);
                }}
              >
                <Edit size={16} className="mr-2.5 text-gray-500 flex-shrink-0" />
                Edit Maintenance
              </button>
            </li>

            <li className="border-t border-gray-200">
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 bg-transparent hover:bg-red-50 border-none cursor-pointer transition-colors text-left"
                onClick={() => {
                  setIsOpen(false);
                  onDelete(maintenance);
                }}
              >
                <Trash2 size={16} className="mr-2.5 text-red-600 flex-shrink-0" />
                Delete Maintenance
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const MaintenanceOperations = () => {
  const { toasts, success, error: showError, removeToast } = useToast();
  
  // Add CSS for animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes slideInFromRight {
        from {
          transform: translateX(100%);
        }
        to {
          transform: translateX(0);
        }
      }
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('maintenance');
  
  // Test mode state
  const [isTestMode, setIsTestMode] = useState(process.env.NODE_ENV === 'development');
  const [testData, setTestData] = useState(null);
  
  // Report filters
  const [reportDateRange, setReportDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  // Real data from API
  const [maintenanceData, setMaintenanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Asset data for dropdown
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);

  // Calendar navigation state
  const [currentDate, setCurrentDate] = useState(new Date());

  // Load maintenance data from API
  useEffect(() => {
    loadMaintenanceData();
  }, [searchTerm, filterStatus, filterType, filterPriority, pagination.currentPage]);

  // Load assets separately to avoid dependency issues
  useEffect(() => {
    loadAssets();
  }, []);

  const loadMaintenanceData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        search: searchTerm || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        type: filterType !== 'all' ? filterType : undefined,
        priority: filterPriority !== 'all' ? filterPriority : undefined
      };

      const response = await MaintenanceService.getMaintenanceRecords(params);
      
      if (response.success) {
        setMaintenanceData(response.data);
        setPagination(prev => ({
          ...prev,
          totalPages: response.pagination.totalPages,
          totalItems: response.pagination.totalItems
        }));
      }
    } catch (err) {
      console.error('Error loading maintenance data:', err);
      setError('Failed to load maintenance data');
      // Fallback to mock data if API fails
      setMaintenanceData([
    {
      id: 'MNT-001',
      assetId: 'AST-007',
      assetName: 'Dell Latitude 5520',
      type: 'Preventive',
      scheduledDate: '2024-02-15',
      completedDate: '2024-02-15',
      technician: 'John Smith',
      status: 'Completed',
      priority: 'Medium',
      description: 'Regular maintenance check and software updates',
      cost: 150,
      vendor: 'Tech Solutions Inc.',
      notes: 'All systems functioning properly',
      location: 'IT Department',
      estimatedDuration: '2 hours',
      actualDuration: '1.5 hours',
      lastUpdated: '2024-02-15T14:30:00Z'
    },
    {
      id: 'MNT-002',
      assetId: 'AST-005',
      assetName: 'Warehouse Facility',
      type: 'Corrective',
      scheduledDate: '2024-02-20',
      completedDate: null,
      technician: 'Mike Johnson',
      status: 'In Progress',
      priority: 'High',
      description: 'HVAC system repair and calibration',
      cost: 500,
      vendor: 'HVAC Pro Services',
      notes: 'Emergency repair needed for temperature control',
      location: 'Warehouse Building A',
      estimatedDuration: '4 hours',
      actualDuration: null,
      lastUpdated: '2024-02-20T09:15:00Z'
    },
    {
      id: 'MNT-003',
      assetId: 'AST-004',
      assetName: 'IT Equipment - Server Rack',
      type: 'Preventive',
      scheduledDate: '2024-02-25',
      completedDate: null,
      technician: 'Sarah Wilson',
      status: 'Scheduled',
      priority: 'Low',
      description: 'Server maintenance and backup verification',
      cost: 200,
      vendor: 'IT Maintenance Co.',
      notes: 'Routine quarterly maintenance',
      location: 'Data Center',
      estimatedDuration: '3 hours',
      actualDuration: null,
      lastUpdated: '2024-02-10T16:45:00Z'
    },
    {
      id: 'MNT-004',
      assetId: 'AST-008',
      assetName: 'MacBook Pro 16"',
      type: 'Repair',
      scheduledDate: '2024-02-18',
      completedDate: '2024-02-18',
      technician: 'David Lee',
      status: 'Completed',
      priority: 'High',
      description: 'Screen replacement and battery check',
      cost: 350,
      vendor: 'Apple Authorized Service',
      notes: 'Screen cracked due to accidental damage',
      location: 'Design Studio',
      estimatedDuration: '2 hours',
      actualDuration: '2.5 hours',
      lastUpdated: '2024-02-18T17:20:00Z'
    },
    {
      id: 'MNT-005',
      assetId: 'AST-009',
      assetName: 'Company Vehicle - Honda Civic',
      type: 'Preventive',
      scheduledDate: '2024-02-22',
      completedDate: null,
      technician: 'Auto Service Center',
      status: 'Scheduled',
      priority: 'Medium',
      description: 'Oil change, tire rotation, and inspection',
      cost: 120,
      vendor: 'Quick Lube Plus',
      notes: 'Regular 10,000 mile service',
      location: 'Company Parking Lot',
      estimatedDuration: '1 hour',
      actualDuration: null,
      lastUpdated: '2024-02-12T11:30:00Z'
    }
  ]);
    } finally {
      setLoading(false);
    }
  };

  // Load assets for dropdown
  const loadAssets = async () => {
    try {
      setLoadingAssets(true);
      
      const response = await assetService.getAssets({ 
        status: 'Active',
        limit: 100 // API maximum limit is 100
      });
      
      // Handle different response structures
      let assetsArray = [];
      
      if (response?.success && response?.data?.assets) {
        assetsArray = response.data.assets;
      } else if (response?.data && Array.isArray(response.data)) {
        assetsArray = response.data;
      } else if (Array.isArray(response)) {
        assetsArray = response;
      } else if (response?.assets && Array.isArray(response.assets)) {
        assetsArray = response.assets;
      }
      
      if (assetsArray.length > 0) {
        // Transform assets to match the expected format
        const transformedAssets = assetsArray.map(asset => ({
          assetId: asset.assetCode || asset.assetId || asset._id?.toString() || asset.id?.toString() || '',
          assetCode: asset.assetCode || asset.assetId || '',
          _id: asset._id?.toString() || asset.id?.toString() || '',
          name: asset.name || 'Unnamed Asset',
          assetName: asset.name || 'Unnamed Asset',
          type: asset.type || 'Other'
        }));
        
        setAssets(transformedAssets);
      } else {
        // No assets found - set empty array instead of fallback
        setAssets([]);
      }
    } catch (err) {
      console.error('Error loading assets:', err);
      // On error, set empty array - user can refresh
      setAssets([]);
    } finally {
      setLoadingAssets(false);
    }
  };

  // Calendar navigation functions
  const navigateCalendar = (direction) => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (direction === 'next') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (direction === 'today') {
      newDate.setTime(Date.now());
    }
    setCurrentDate(newDate);
  };

  const handleScheduleNew = () => {
    navigate('/assets/maintenance/create');
  };

  const handleDayClick = (date) => {
    navigate('/assets/maintenance/create', {
      state: {
        scheduledDate: date.toISOString().split('T')[0]
      }
    });
  };

  // Load maintenance statistics
  const [stats, setStats] = useState({
    totalMaintenance: 0,
    completedMaintenance: 0,
    inProgressMaintenance: 0,
    scheduledMaintenance: 0,
    totalCost: 0
  });

  useEffect(() => {
    loadMaintenanceStats();
  }, []);

  const loadMaintenanceStats = async () => {
    try {
      const response = await MaintenanceService.getMaintenanceStats();
      if (response.success) {
        setStats(response.data.overview);
      }
    } catch (err) {
      console.error('Error loading maintenance stats:', err);
      // Calculate stats from local data as fallback
      setStats({
        totalMaintenance: maintenanceData.length,
        completedMaintenance: maintenanceData.filter(m => m.status === 'Completed').length,
        inProgressMaintenance: maintenanceData.filter(m => m.status === 'In Progress').length,
        scheduledMaintenance: maintenanceData.filter(m => m.status === 'Scheduled').length,
        totalCost: maintenanceData.reduce((sum, m) => sum + (m.cost || 0), 0)
      });
    }
  };

  // Form state for new maintenance record
  const [formData, setFormData] = useState({
    assetId: '',
    type: 'Preventive',
    description: '',
    scheduledDate: new Date().toISOString().split('T')[0],
    completedDate: '',
    status: 'Scheduled',
    priority: 'Medium',
    cost: 0,
    vendor: '',
    technician: '',
    notes: '',
    location: '',
    estimatedDuration: ''
  });

  // Filtered maintenance data (now handled by API)
  const filteredMaintenance = maintenanceData;

  const tabs = [
    { id: 'maintenance', label: 'Maintenance Records' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'reports', label: 'Reports' }
  ];

  // Define columns for DataTable - Optimized for better layout
  const columns = [
    {
      key: 'maintenanceId',
      label: 'ID',
      width: '100px',
      render: (value, row) => (
        <div className="d-flex align-items-center gap-1">
          <Wrench size={14} style={{ color: 'var(--primary-color)' }} />
          <strong style={{ fontSize: '12px' }}>{value || 'No ID'}</strong>
        </div>
      )
    },
    {
      key: 'asset',
      label: 'Asset Name',
      width: '200px',
      render: (value, row) => (
        <div>
          <div className="fw-medium" style={{ fontSize: '14px' }}>
            {value?.assetName || 'No Asset Name'}
          </div>
          <small className="text-muted" style={{ fontSize: '11px' }}>
            {value?.assetId || 'No ID'}
          </small>
        </div>
      )
    },
    {
      key: 'type',
      label: 'Type',
      width: '120px',
      type: 'badge',
      badgeClass: (value) => {
        const typeClasses = {
          'Preventive': 'badge-primary',
          'Corrective': 'badge-warning',
          'Repair': 'badge-danger',
          'Emergency': 'badge-secondary'
        };
        return typeClasses[value] || 'badge-secondary';
      }
    },
    {
      key: 'status',
      label: 'Status',
      width: '130px',
      type: 'badge',
      badgeClass: (value) => {
        const statusClasses = {
          'Completed': 'badge-success',
          'In Progress': 'badge-warning',
          'Scheduled': 'badge-info',
          'Cancelled': 'badge-danger'
        };
        return statusClasses[value] || 'badge-secondary';
      }
    },
    {
      key: 'technician',
      label: 'Technician',
      width: '140px',
      render: (value) => (
        <div className="d-flex align-items-center gap-1">
          <User size={12} style={{ color: 'var(--text-muted)' }} />
          <span style={{ fontSize: '13px' }}>
            {typeof value === 'object' && value?.name ? value.name : (value || 'Unassigned')}
          </span>
        </div>
      )
    },
    {
      key: 'cost',
      label: 'Cost',
      width: '120px',
      type: 'currency',
      render: (value) => (
        <div className="d-flex align-items-center gap-1">
          <DollarSign size={12} style={{ color: 'var(--success-color)' }} />
          <span style={{ fontSize: '13px', fontWeight: '600' }}>${value.toLocaleString()}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '60px',
      render: (_, maintenance) => (
      <div className="d-flex justify-content-center">
        <ActionDropdown
        maintenance={maintenance}
        onView={handleViewMaintenance}
        onEdit={handleEditMaintenance}
        onDelete={handleDeleteMaintenance}
       />
      </div>
      )
    }
  ];

  // Handler functions
  const handleViewMaintenance = (maintenance) => {
    setSelectedMaintenance(maintenance);
    setShowViewModal(true);
  };

  const handleEditMaintenance = (maintenance) => {
    setSelectedMaintenance(maintenance);
    
    // Helper function to format date for HTML date input
    const formatDateForInput = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
    };
    
    setFormData({
      assetId: maintenance.asset?.assetId || maintenance.assetId,
      type: maintenance.type,
      description: maintenance.description,
      scheduledDate: formatDateForInput(maintenance.scheduledDate),
      completedDate: formatDateForInput(maintenance.completedDate),
      status: maintenance.status,
      priority: maintenance.priority,
      cost: maintenance.cost,
      vendor: typeof maintenance.vendor === 'object' && maintenance.vendor?.name ? maintenance.vendor.name : maintenance.vendor,
      technician: typeof maintenance.technician === 'object' && maintenance.technician?.name ? maintenance.technician.name : maintenance.technician,
      notes: maintenance.notes || '',
      location: maintenance.asset?.location || maintenance.location || '',
      estimatedDuration: maintenance.estimatedDuration || ''
    });
    setShowEditModal(true);
  };

  const handleDeleteMaintenance = (maintenance) => {
    setSelectedMaintenance(maintenance);
    setShowDeleteModal(true);
  };

  const handleAddNew = () => {
    navigate('/assets/maintenance/create');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate required fields
      if (!formData.assetId) {
        showError('Asset ID is required');
        return;
      }
      if (!formData.description) {
        showError('Description is required');
        return;
      }
      if (!formData.scheduledDate) {
        showError('Scheduled Date is required');
        return;
      }

      // Transform form data to match API expectations
      const apiData = {
        ...formData,
        maintenanceId: formData.assetId ? `${formData.assetId}-MNT-${Date.now()}` : `MNT-${Date.now()}`,
        technician: formData.technician ? {
          name: formData.technician,
          contact: ''
        } : null,
        vendor: formData.vendor ? {
          name: formData.vendor,
          contact: '',
          phone: ''
        } : null,
        cost: parseFloat(formData.cost) || 0
      };

      // Always include asset object (required by schema)
      // The backend will validate and fetch asset details if needed
      if (formData.assetId) {
        apiData.asset = {
          assetId: formData.assetId,
          assetName: formData.assetId ? `Asset ${formData.assetId}` : 'Unknown Asset',
          assetType: 'Equipment',
          location: formData.location || 'Unknown Location'
        };
      }
      
      // Remove assetId from top level as it should be in asset object
      delete apiData.assetId;

      if (showEditModal && selectedMaintenance) {
        // Update existing maintenance record
        const response = await MaintenanceService.updateMaintenance(selectedMaintenance._id, apiData);
        if (response.success) {
          await loadMaintenanceData(); // Reload data from API
          await loadMaintenanceStats(); // Reload stats
          setShowEditModal(false);
          // Show success message
          success('Maintenance record updated successfully!');
        }
      }
      
      // Reset form
      setFormData({
        assetId: '',
        type: 'Preventive',
        description: '',
        scheduledDate: new Date().toISOString().split('T')[0],
        completedDate: '',
        status: 'Scheduled',
        priority: 'Medium',
        cost: 0,
        vendor: '',
        technician: '',
        notes: '',
        location: '',
        estimatedDuration: ''
      });
    } catch (error) {
      console.error('Error saving maintenance record:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      // Show detailed error message
      let errorMessage = 'Failed to save maintenance record';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.errors && Array.isArray(errorData.errors)) {
          // Show validation errors
          const errorList = errorData.errors.map(err => `${err.field}: ${err.message}`).join(', ');
          errorMessage = `Validation errors: ${errorList}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showError(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await MaintenanceService.deleteMaintenance(selectedMaintenance._id);
      await loadMaintenanceData(); // Reload data from API
      await loadMaintenanceStats(); // Reload stats
      setShowDeleteModal(false);
      setSelectedMaintenance(null);
      // Show success message
      success('Maintenance record deleted successfully!');
    } catch (error) {
      console.error('Error deleting maintenance record:', error);
      // Show error message
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete maintenance record';
      showError(`Error: ${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Test data generation functions
  const generateTestMaintenanceData = () => {
    const testMaintenanceRecords = [
      {
        assetId: `TEST-${Date.now()}`,
        type: 'Preventive',
        description: 'Test maintenance - Regular system check and software updates',
        scheduledDate: new Date().toISOString().split('T')[0],
        completedDate: new Date().toISOString().split('T')[0],
        status: 'Completed',
        priority: 'Medium',
        cost: 150,
        vendor: 'Test Solutions Inc.',
        technician: 'Test Technician',
        notes: 'This is a test maintenance record for automated testing purposes.',
        location: 'Test Department',
        estimatedDuration: '2 hours'
      },
      {
        assetId: `TEST-${Date.now() + 1}`,
        type: 'Corrective',
        description: 'Test maintenance - Emergency repair and system calibration',
        scheduledDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow
        completedDate: '',
        status: 'Scheduled',
        priority: 'High',
        cost: 300,
        vendor: 'Emergency Test Services',
        technician: 'Emergency Test Tech',
        notes: 'Test emergency maintenance record for validation testing.',
        location: 'Test Emergency Room',
        estimatedDuration: '4 hours'
      },
      {
        assetId: `TEST-${Date.now() + 2}`,
        type: 'Inspection',
        description: 'Test maintenance - Quarterly safety inspection and compliance check',
        scheduledDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Next week
        completedDate: '',
        status: 'Scheduled',
        priority: 'Low',
        cost: 75,
        vendor: 'Safety Test Corp',
        technician: 'Safety Inspector',
        notes: 'Test inspection record for compliance validation.',
        location: 'Test Safety Zone',
        estimatedDuration: '1 hour'
      }
    ];
    
    return testMaintenanceRecords[Math.floor(Math.random() * testMaintenanceRecords.length)];
  };

  // Test feature handlers
  const handleTestFeature = (action) => {
    switch (action) {
      case 'auto-fill':
        // Auto-fill the form with test data and navigate to create page
        const generatedTestData = generateTestMaintenanceData();
        setTestData(generatedTestData);
        navigate('/assets/maintenance/create', { state: { formData: generatedTestData } });
        success('Test data auto-filled! Form is ready for testing.');
        break;
      case 'create-test':
        // Create a test maintenance record
        const testData = generateTestMaintenanceData();
        navigate('/assets/maintenance/create', { state: { formData: testData } });
        success('Test form opened with sample data. Click Save to create test record.');
        break;
      case 'validate-form':
        // Validate current form data
        const validationErrors = [];
        if (!formData.assetId) validationErrors.push('Asset ID is required');
        if (!formData.description) validationErrors.push('Description is required');
        if (!formData.scheduledDate) validationErrors.push('Scheduled Date is required');
        if (!formData.type) validationErrors.push('Type is required');
        if (!formData.status) validationErrors.push('Status is required');
        if (!formData.priority) validationErrors.push('Priority is required');
        
        if (validationErrors.length > 0) {
          showError(`Form validation failed: ${validationErrors.join(', ')}`);
        } else {
          success('Form validation passed! All required fields are filled.');
        }
        break;
      case 'reset-form':
        // Reset form to default state
        setFormData({
          assetId: '',
          type: 'Preventive',
          description: '',
          scheduledDate: new Date().toISOString().split('T')[0],
          completedDate: '',
          status: 'Scheduled',
          priority: 'Medium',
          cost: 0,
          vendor: '',
          technician: '',
          notes: '',
          location: '',
          estimatedDuration: ''
        });
        setTestData(null);
        success('Form reset to default state.');
        break;
      default:
        console.log('Unknown test action:', action);
    }
  };

  // Filter maintenance data for reports
  const getFilteredMaintenanceData = () => {
    let filtered = maintenanceData;
    
    if (reportDateRange.startDate) {
      filtered = filtered.filter(m => 
        new Date(m.scheduledDate) >= new Date(reportDateRange.startDate)
      );
    }
    
    if (reportDateRange.endDate) {
      filtered = filtered.filter(m => 
        new Date(m.scheduledDate) <= new Date(reportDateRange.endDate)
      );
    }
    
    return filtered;
  };

  // Export report functionality
  const handleExportReport = () => {
    try {
      // Create CSV content
      const csvHeaders = [
        'Maintenance ID',
        'Asset Name',
        'Asset ID',
        'Type',
        'Status',
        'Priority',
        'Description',
        'Scheduled Date',
        'Completed Date',
        'Cost',
        'Vendor',
        'Technician',
        'Location',
        'Estimated Duration',
        'Notes'
      ];

      const csvRows = maintenanceData.map(maintenance => [
        maintenance.maintenanceId || '',
        maintenance.asset?.assetName || maintenance.assetName || '',
        maintenance.asset?.assetId || maintenance.assetId || '',
        maintenance.type || '',
        maintenance.status || '',
        maintenance.priority || '',
        maintenance.description || '',
        maintenance.scheduledDate ? new Date(maintenance.scheduledDate).toLocaleDateString() : '',
        maintenance.completedDate ? new Date(maintenance.completedDate).toLocaleDateString() : '',
        maintenance.cost || 0,
        typeof maintenance.vendor === 'object' ? maintenance.vendor?.name || '' : maintenance.vendor || '',
        typeof maintenance.technician === 'object' ? maintenance.technician?.name || '' : maintenance.technician || '',
        maintenance.asset?.location || maintenance.location || '',
        maintenance.estimatedDuration || '',
        maintenance.notes || ''
      ]);

      // Combine headers and rows
      const csvContent = [csvHeaders, ...csvRows]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `maintenance-report-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      success('Maintenance report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      showError('Failed to export report. Please try again.');
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    await loadMaintenanceData();
    await loadMaintenanceStats();
    success('Maintenance data refreshed successfully');
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/maintenance/maintenance-operations';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={(isLoading || loading) ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh maintenance data',
      disabled: isLoading || loading
    },
    {
      label: 'New Maintenance',
      icon: <Plus size={18} />,
      onClick: handleAddNew,
      variant: 'primary',
      tooltip: 'Create a new maintenance record'
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Maintenance & Operations"
        description="Schedule and track asset maintenance activities"
        actions={headerActions}
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Tabbed Navigation */}
        <div className="project-tabs">
          <div className="project-tabs-nav">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="project-tab-icon">
                  {tab.id === 'maintenance' && <Wrench size={18} />}
                  {tab.id === 'schedule' && <Calendar size={18} />}
                  {tab.id === 'reports' && <FileText size={18} />}
                </div>
                <div className="project-tab-content">
                  <div className="project-tab-label">{tab.label}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

      {/* Main Content */}
      <div className="row">
        <div className="col-12" style={{ overflowX: 'hidden' }}>
          <>
            {activeTab === 'maintenance' && (
              <div className="project-tab-content">
                <DataTable
                  data={filteredMaintenance}
                  columns={columns}
                  title="Maintenance Records"
                  searchable={true}
                  sortable={true}
                  exportable={true}
                  pagination={true}
                  pageSize={10}
                  showActions={false}
                  showCheckboxes={false}
                  //onView={handleViewMaintenance}
                  //onEdit={handleEditMaintenance}
                  //onDelete={handleDeleteMaintenance}
                  searchPlaceholder="Search maintenance records..."
                  loading={isLoading}
                  emptyMessage="No maintenance records found. Click 'New Maintenance' to get started."
                  initialHiddenColumns={['none']}
                />
              </div>
            )}

            {activeTab === 'schedule' && (
            <div className="project-tab-content">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0
              }}>
                Maintenance Schedule
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => navigateCalendar('prev')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 8px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  ←
                </button>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  minWidth: '120px',
                  textAlign: 'center'
                }}>
                  {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <button
                  onClick={() => navigateCalendar('next')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 8px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  →
                </button>
                <button
                  onClick={() => navigateCalendar('today')}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#007B8F',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Today
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleScheduleNew}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  backgroundColor: '#007B8F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                <Plus size={14} />
                Schedule New
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '1px',
            backgroundColor: '#e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            {/* Calendar Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} style={{
                backgroundColor: '#f9fafb',
                padding: '12px 8px',
                textAlign: 'center',
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                borderBottom: '1px solid #e5e7eb'
              }}>
                {day}
              </div>
            ))}

            {/* Calendar Days */}
            {Array.from({ length: 35 }, (_, i) => {
              const day = i - 6; // Start from -6 to show previous month's days
              const currentMonth = currentDate.getMonth();
              const currentYear = currentDate.getFullYear();
              const firstDay = new Date(currentYear, currentMonth, 1);
              const lastDay = new Date(currentYear, currentMonth + 1, 0);
              const daysInMonth = lastDay.getDate();
              const startDay = firstDay.getDay();

              let displayDay = day - startDay + 1;
              let isCurrentMonth = true;
              let isToday = false;

              if (displayDay <= 0) {
                displayDay = new Date(currentYear, currentMonth, 0).getDate() + displayDay;
                isCurrentMonth = false;
              } else if (displayDay > daysInMonth) {
                displayDay = displayDay - daysInMonth;
                isCurrentMonth = false;
              }

              const today = new Date();
              isToday = isCurrentMonth && displayDay === today.getDate() && 
                       currentMonth === today.getMonth() && 
                       currentYear === today.getFullYear();

              // Create date object for this day
              const dayDate = new Date(currentYear, currentMonth, displayDay);

              // Find maintenance records for this day
              const dayMaintenance = maintenanceData.filter(maintenance => {
                const maintenanceDate = new Date(maintenance.scheduledDate);
                return isCurrentMonth && 
                       maintenanceDate.getDate() === displayDay &&
                       maintenanceDate.getMonth() === currentMonth &&
                       maintenanceDate.getFullYear() === currentYear;
              });

              return (
                <div
                  key={i}
                  onClick={() => isCurrentMonth && handleDayClick(dayDate)}
                  style={{
                    backgroundColor: 'white',
                    minHeight: '80px',
                    padding: '8px',
                    border: '1px solid #e5e7eb',
                    position: 'relative',
                    opacity: isCurrentMonth ? 1 : 0.4,
                    cursor: isCurrentMonth ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                    ':hover': isCurrentMonth ? {
                      backgroundColor: '#f8fafc',
                      borderColor: '#007B8F'
                    } : {}
                  }}
                  onMouseEnter={(e) => {
                    if (isCurrentMonth) {
                      e.target.style.backgroundColor = '#f8fafc';
                      e.target.style.borderColor = '#007B8F';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isCurrentMonth) {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.borderColor = '#e5e7eb';
                    }
                  }}
                  title={isCurrentMonth ? `Click to schedule maintenance for ${dayDate.toLocaleDateString()}` : ''}
                >
                  <div style={{
                    fontSize: '12px',
                    fontWeight: isToday ? '600' : '400',
                    color: isToday ? '#007B8F' : '#374151',
                    marginBottom: '4px'
                  }}>
                    {displayDay}
                  </div>
                  
                  {/* Maintenance items for this day */}
                  {dayMaintenance.slice(0, 2).map((maintenance, idx) => (
                    <div
                      key={idx}
                      style={{
                        fontSize: '10px',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        marginBottom: '2px',
                        backgroundColor: maintenance.status === 'Completed' ? '#d1fae5' :
                                       maintenance.status === 'In Progress' ? '#fef3c7' :
                                       maintenance.status === 'Scheduled' ? '#dbeafe' : '#fee2e2',
                        color: maintenance.status === 'Completed' ? '#065f46' :
                               maintenance.status === 'In Progress' ? '#92400e' :
                               maintenance.status === 'Scheduled' ? '#1e40af' : '#991b1b',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={() => handleViewMaintenance(maintenance)}
                      title={`${maintenance.id} - ${maintenance.assetName}`}
                    >
                      {maintenance.id}
                    </div>
                  ))}
                  
                  {dayMaintenance.length > 2 && (
                    <div style={{
                      fontSize: '10px',
                      color: '#6b7280',
                      textAlign: 'center',
                      marginTop: '2px'
                    }}>
                      +{dayMaintenance.length - 2} more
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{
            display: 'flex',
            gap: '16px',
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '6px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#dbeafe',
                borderRadius: '2px'
              }}></div>
              <span style={{ fontSize: '12px', color: '#374151' }}>Scheduled</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#fef3c7',
                borderRadius: '2px'
              }}></div>
              <span style={{ fontSize: '12px', color: '#374151' }}>In Progress</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#d1fae5',
                borderRadius: '2px'
              }}></div>
              <span style={{ fontSize: '12px', color: '#374151' }}>Completed</span>
            </div>
          </div>
          </div>
          )}

          {activeTab === 'reports' && (
            <div className="project-tab-content">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: 0
            }}>
              Maintenance Reports
            </h3>
            <button
              onClick={handleExportReport}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              <Download size={14} />
              Export Report
            </button>
          </div>

          {/* Report Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px',
            marginBottom: '24px'
          }}>
            {/* Cost Analysis Report */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '16px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 12px 0'
              }}>
                Cost Analysis
              </h4>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Total Spent:</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>
                  ${stats.totalCost.toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Average Cost:</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>
                  ${Math.round(stats.totalCost / stats.totalMaintenance).toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Records:</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>
                  {stats.totalMaintenance}
                </span>
              </div>
            </div>

            {/* Status Distribution Report */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '16px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 12px 0'
              }}>
                Status Distribution
              </h4>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Completed:</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#10b981' }}>
                  {stats.completedMaintenance} ({Math.round((stats.completedMaintenance / stats.totalMaintenance) * 100)}%)
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>In Progress:</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#f59e0b' }}>
                  {stats.inProgressMaintenance} ({Math.round((stats.inProgressMaintenance / stats.totalMaintenance) * 100)}%)
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Scheduled:</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#3b82f6' }}>
                  {stats.scheduledMaintenance} ({Math.round((stats.scheduledMaintenance / stats.totalMaintenance) * 100)}%)
                </span>
              </div>
            </div>

            {/* Type Analysis Report */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '16px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 12px 0'
              }}>
                Maintenance Types
              </h4>
              {['Preventive', 'Corrective', 'Repair', 'Emergency'].map(type => {
                const count = maintenanceData.filter(m => m.type === type).length;
                const percentage = Math.round((count / stats.totalMaintenance) * 100);
                return (
                  <div key={type} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>{type}:</span>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#1f2937' }}>
                      {count} ({percentage}%)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detailed Reports Table */}
          <div style={{
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            padding: '16px',
            border: '1px solid #e5e7eb'
          }}>
            <h4 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 16px 0'
            }}>
              Recent Maintenance Activities
            </h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '12px'
              }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '8px', fontWeight: '600', color: '#374151' }}>ID</th>
                    <th style={{ textAlign: 'left', padding: '8px', fontWeight: '600', color: '#374151' }}>Asset</th>
                    <th style={{ textAlign: 'left', padding: '8px', fontWeight: '600', color: '#374151' }}>Type</th>
                    <th style={{ textAlign: 'left', padding: '8px', fontWeight: '600', color: '#374151' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '8px', fontWeight: '600', color: '#374151' }}>Cost</th>
                    <th style={{ textAlign: 'left', padding: '8px', fontWeight: '600', color: '#374151' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {maintenanceData.slice(0, 10).map((maintenance) => (
                    <tr key={maintenance._id || maintenance.maintenanceId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px', color: '#1f2937' }}>{maintenance.maintenanceId || 'No ID'}</td>
                      <td style={{ padding: '8px', color: '#1f2937' }}>{maintenance.asset?.assetName || maintenance.assetName || 'No Asset'}</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: '500',
                          backgroundColor: maintenance.type === 'Preventive' ? '#dbeafe' : '#fef3c7',
                          color: maintenance.type === 'Preventive' ? '#1e40af' : '#92400e'
                        }}>
                          {maintenance.type}
                        </span>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: '500',
                          backgroundColor: maintenance.status === 'Completed' ? '#d1fae5' : '#fef3c7',
                          color: maintenance.status === 'Completed' ? '#065f46' : '#92400e'
                        }}>
                          {maintenance.status}
                        </span>
                      </td>
                      <td style={{ padding: '8px', color: '#1f2937', fontWeight: '500' }}>
                        ${maintenance.cost.toLocaleString()}
                      </td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>
                        {new Date(maintenance.scheduledDate).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>
          )}
          </>
        </div>
      </div>

      {/* Edit Maintenance Modal - Full Viewport Side Panel */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          animation: 'fadeIn 0.2s ease-in-out'
        }}>
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '100%',
            maxWidth: '800px',
            height: '100vh',
            backgroundColor: 'white',
            boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            animation: 'slideInFromRight 0.3s ease-out',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              backgroundColor: 'var(--primary-color)',
              color: 'white'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: 'white',
                margin: 0,
                fontFamily: 'var(--font-family)'
              }}>
                Edit Maintenance Record
              </h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'white'
                }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ 
              padding: '24px',
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden'
            }}>

            <form onSubmit={handleFormSubmit}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '6px'
                  }}>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151'
                    }}>
                      Asset *
                    </label>
                    <button
                      type="button"
                      onClick={loadAssets}
                      disabled={loadingAssets}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '12px',
                      cursor: loadingAssets ? 'not-allowed' : 'pointer',
                      opacity: loadingAssets ? 0.6 : 1,
                      fontFamily: 'var(--font-family)'
                    }}
                    >
                      <RefreshCw size={12} style={{
                        animation: loadingAssets ? 'spin 1s linear infinite' : 'none'
                      }} />
                      {loadingAssets ? 'Loading...' : 'Refresh'}
                    </button>
                  </div>
                  <select
                    name="assetId"
                    value={formData.assetId}
                    onChange={handleFormChange}
                    required
                    disabled={loadingAssets}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                      backgroundColor: loadingAssets ? '#f9fafb' : 'white',
                      cursor: loadingAssets ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">
                      {loadingAssets ? 'Loading assets...' : `Select an asset (${assets.length} available)`}
                    </option>
                    {assets.map((asset) => {
                      const assetId = asset.assetId || asset.assetCode || asset._id || asset.id || '';
                      const assetName = asset.name || asset.assetName || 'Unnamed Asset';
                      const displayText = asset.assetCode || asset.assetId ? 
                        `${asset.assetCode || asset.assetId} - ${assetName}` : 
                        assetName;
                      return (
                        <option key={assetId || asset._id || asset.id} value={assetId}>
                          {displayText}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Type *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  >
                    <option value="Preventive">Preventive</option>
                    <option value="Corrective">Corrective</option>
                    <option value="Repair">Repair</option>
                    <option value="Emergency">Emergency</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Status *
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  >
                    <option value="Scheduled">Scheduled</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Priority *
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  >
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Scheduled Date *
                  </label>
                  <input
                    type="date"
                    name="scheduledDate"
                    value={formData.scheduledDate}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Completed Date
                  </label>
                  <input
                    type="date"
                    name="completedDate"
                    value={formData.completedDate}
                    onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Cost
                  </label>
                  <input
                    type="number"
                    name="cost"
                    value={formData.cost}
                    onChange={handleFormChange}
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Technician
                  </label>
                  <input
                    type="text"
                    name="technician"
                    value={formData.technician}
                    onChange={handleFormChange}
                    placeholder="e.g., John Smith"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Vendor
                  </label>
                  <input
                    type="text"
                    name="vendor"
                    value={formData.vendor}
                    onChange={handleFormChange}
                    placeholder="e.g., Tech Solutions Inc."
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleFormChange}
                    placeholder="e.g., IT Department"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Estimated Duration
                  </label>
                  <input
                    type="text"
                    name="estimatedDuration"
                    value={formData.estimatedDuration}
                    onChange={handleFormChange}
                    placeholder="e.g., 2 hours"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Description *
                  </label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="e.g., Regular maintenance check and software updates"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    placeholder="Additional notes or comments..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setShowEditModal(false);
                  }}
                  style={{
                    borderRadius: '0'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '0',
                    opacity: isLoading ? 0.6 : 1
                  }}
                >
                  {isLoading ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Update Maintenance
                    </>
                  )}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* View Maintenance Modal */}
      {showViewModal && selectedMaintenance && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0',
            padding: '0',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              backgroundColor: 'var(--primary-color)',
              color: 'white'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: 'white',
                margin: 0,
                fontFamily: 'var(--font-family)'
              }}>
                Maintenance Details
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'white'
                }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '24px' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  Maintenance ID
                </label>
                <div style={{
                  fontSize: '14px',
                  color: '#1f2937',
                  fontWeight: '500'
                }}>
                  {selectedMaintenance.id}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  Asset
                </label>
                <div style={{
                  fontSize: '14px',
                  color: '#1f2937',
                  fontWeight: '500'
                }}>
                  {selectedMaintenance.asset?.assetId || selectedMaintenance.assetId} - {selectedMaintenance.asset?.assetName || selectedMaintenance.assetName}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  Type
                </label>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 8px',
                  borderRadius: '0',
                  fontSize: '12px',
                  fontWeight: '500',
                  backgroundColor: selectedMaintenance.type === 'Preventive' ? '#dbeafe' : '#fef3c7',
                  color: selectedMaintenance.type === 'Preventive' ? '#1e40af' : '#92400e'
                }}>
                  {selectedMaintenance.type}
                </span>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  Status
                </label>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 8px',
                  borderRadius: '0',
                  fontSize: '12px',
                  fontWeight: '500',
                  backgroundColor: selectedMaintenance.status === 'Completed' ? '#d1fae5' : '#fef3c7',
                  color: selectedMaintenance.status === 'Completed' ? '#065f46' : '#92400e'
                }}>
                  {selectedMaintenance.status}
                </span>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  Priority
                </label>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 8px',
                  borderRadius: '0',
                  fontSize: '12px',
                  fontWeight: '500',
                  backgroundColor: selectedMaintenance.priority === 'High' ? '#fee2e2' : '#fef3c7',
                  color: selectedMaintenance.priority === 'High' ? '#991b1b' : '#92400e'
                }}>
                  {selectedMaintenance.priority}
                </span>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  Scheduled Date
                </label>
                <div style={{
                  fontSize: '14px',
                  color: '#1f2937'
                }}>
                  {new Date(selectedMaintenance.scheduledDate).toLocaleDateString()}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  Technician
                </label>
                <div style={{
                  fontSize: '14px',
                  color: '#1f2937'
                }}>
                  {typeof selectedMaintenance.technician === 'object' && selectedMaintenance.technician?.name ? selectedMaintenance.technician.name : selectedMaintenance.technician}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  Cost
                </label>
                <div style={{
                  fontSize: '14px',
                  color: '#1f2937',
                  fontWeight: '500'
                }}>
                  ${selectedMaintenance.cost.toLocaleString()}
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#6b7280',
                  marginBottom: '4px'
                }}>
                  Description
                </label>
                <div style={{
                  fontSize: '14px',
                  color: '#1f2937'
                }}>
                  {selectedMaintenance.description}
                </div>
              </div>

              {selectedMaintenance.notes && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6b7280',
                    marginBottom: '4px'
                  }}>
                    Notes
                  </label>
                  <div style={{
                    fontSize: '14px',
                    color: '#1f2937',
                    backgroundColor: '#f9fafb',
                    padding: '12px',
                    borderRadius: '0',
                    border: '1px solid #e5e7eb'
                  }}>
                    {selectedMaintenance.notes}
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Maintenance Record"
        message={`Are you sure you want to delete maintenance record ${selectedMaintenance?.id}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default MaintenanceOperations;
